import React, { useCallback, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import type { CustomerIndexItemDto } from '@techgitter/contracts';
import { duplicateQuote } from '../../services/quotesApi';
import { CustomerLookupControl } from '../tickets/Customer/CustomerLookupControl';

interface DuplicateQuoteDialogProps {
  /** The quote being duplicated. Null closes the dialog. */
  source: { number: string } | null;
  onClose: () => void;
  onDuplicated: (quoteNumber: string) => void;
}

/**
 * Duplicate a quote for a DIFFERENT customer. Picks the target customer and
 * calls POST /api/quotes/{number}/duplicate, which copies the line items and
 * pricing while dropping the source-customer ticket/check-in linkage. Shared
 * by the quotes list (row action) and the quote detail page (action bar).
 */
export const DuplicateQuoteDialog: React.FC<DuplicateQuoteDialogProps> = ({
  source,
  onClose,
  onDuplicated,
}) => {
  const [customer, setCustomer] = useState<CustomerIndexItemDto | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setCustomer(null);
    setError(null);
    onClose();
  }, [onClose]);

  const handleDuplicate = async () => {
    if (!source || !customer) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await duplicateQuote(
        source.number,
        String(customer.dataverseCustomerId),
      );
      setCustomer(null);
      onDuplicated(created.number);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={Boolean(source)} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Duplicate quote {source?.number}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
          Copies the line items and pricing onto a new draft for a different
          customer. Ticket linkage from the original is not carried over.
        </Typography>
        <CustomerLookupControl
          value={customer}
          onChange={setCustomer}
          disabled={submitting}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleDuplicate}
          disabled={!customer || submitting}
        >
          {submitting ? <CircularProgress size={16} /> : 'Duplicate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateQuoteDialog;
